import { useRef, useState } from 'react'

import { Spinner } from '@/components/ui'

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xlsm', '.csv', '.tsv', '.txt']
const MAX_SIZE_MB = 50

/**
 * Загрузка файла перетаскиванием.
 *
 * Проверки размера и расширения выполняются здесь, до отправки: сообщить об
 * ошибке мгновенно лучше, чем отправить 200 МБ и получить отказ через минуту.
 * Сервер проверяет то же самое повторно — клиентская проверка защитой не
 * является.
 */
export function FileDropzone({
  onSelect,
  isUploading,
  disabled = false,
}: {
  onSelect: (file: File) => void
  isUploading: boolean
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  function validate(file: File): string | null {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      return `Формат «${extension || 'без расширения'}» не поддерживается. Загрузите XLSX или CSV.`
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      const sizeMb = Math.round(file.size / (1024 * 1024))
      return `Файл ${sizeMb} МБ — больше допустимых ${MAX_SIZE_MB} МБ. Разделите его на части.`
    }
    if (file.size === 0) {
      return 'Файл пустой.'
    }
    return null
  }

  function handleFile(file: File | undefined): void {
    if (!file) return
    const error = validate(file)
    setLocalError(error)
    if (!error) onSelect(file)
  }

  const isBlocked = disabled || isUploading

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-disabled={isBlocked}
        onClick={() => !isBlocked && inputRef.current?.click()}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && !isBlocked) {
            event.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!isBlocked) setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragging(false)
          if (!isBlocked) handleFile(event.dataTransfer.files[0])
        }}
        className={`flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          isDragging
            ? 'border-brand-500 bg-brand-50'
            : 'border-slate-300 bg-white hover:border-brand-400 hover:bg-slate-50'
        } ${isBlocked ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        {isUploading ? (
          <>
            <Spinner className="h-7 w-7 text-brand-600" />
            <p className="text-sm font-medium text-slate-700">Загружаем файл…</p>
          </>
        ) : (
          <>
            <svg
              className="h-9 w-9 text-slate-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 16.5V9m0 0L9 12m3-3l3 3M6.75 19.5h10.5a3 3 0 00.75-5.905 4.5 4.5 0 00-8.755-2.06A3.75 3.75 0 006.75 19.5z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-slate-900">
                Перетащите файл или нажмите, чтобы выбрать
              </p>
              <p className="mt-1 text-xs text-slate-500">
                XLSX или CSV, до {MAX_SIZE_MB} МБ. Подойдёт выгрузка из 1С, банка или маркетплейса.
              </p>
            </div>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
      </div>

      {localError && (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800">
          {localError}
        </p>
      )}
    </div>
  )
}
